import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';

function Messages({ user }) {
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [conversation, setConversation] = useState([]);
  const [showNewMessage, setShowNewMessage] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Formulaire nouveau message
  const [newMessage, setNewMessage] = useState({
    subject: '',
    message: '',
    receiver_id: null,
    receiver_type: user.role === 'driver' ? 'admin' : 'driver'
  });

  // Liste des destinataires possibles (pour admin)
  const [drivers, setDrivers] = useState([]);

  useEffect(() => {
    fetchMessages();
    fetchUnreadCount();

    // Si admin, charger la liste des chauffeurs
    if (user.role === 'admin' || user.role === 'manager') {
      fetchDrivers();
    }
  }, []);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/messages', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      console.error('Erreur messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/messages/unread-count', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error('Erreur compteur non lus:', error);
    }
  };

  const fetchDrivers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/drivers', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDrivers(response.data);
    } catch (error) {
      console.error('Erreur chauffeurs:', error);
    }
  };

  const fetchConversation = async (messageId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/messages/conversation/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversation(response.data);

      // Marquer comme lu
      if (!selectedMessage.is_read) {
        await axios.put(`/api/messages/${messageId}/read`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchMessages();
        fetchUnreadCount();
      }
    } catch (error) {
      console.error('Erreur conversation:', error);
    }
  };

  const handleSelectMessage = (message) => {
    setSelectedMessage(message);
    fetchConversation(message.id);
    setShowNewMessage(false);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');

      const payload = {
        subject: newMessage.subject,
        message: newMessage.message,
        receiver_id: newMessage.receiver_id,
        receiver_type: newMessage.receiver_type,
        parent_message_id: selectedMessage?.id || null
      };

      await axios.post('/api/messages', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Reset
      setNewMessage({
        subject: '',
        message: '',
        receiver_id: null,
        receiver_type: user.role === 'driver' ? 'admin' : 'driver'
      });
      setShowNewMessage(false);
      setSelectedMessage(null);

      // Recharger
      fetchMessages();
      alert('Message envoyé avec succès!');
    } catch (error) {
      console.error('Erreur envoi:', error);
      alert('Erreur lors de l\'envoi du message.');
    }
  };

  const handleReply = () => {
    setNewMessage({
      subject: `Re: ${selectedMessage.subject}`,
      message: '',
      receiver_id: selectedMessage.sender_id,
      receiver_type: selectedMessage.sender_type,
      parent_message_id: selectedMessage.id
    });
    setShowNewMessage(true);
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">Chargement des messages...</div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <h2 style={{ margin: 0 }}>
            💬 Messages
            {unreadCount > 0 && (
              <span style={{
                marginLeft: '1rem',
                background: '#e74c3c',
                color: 'white',
                padding: '0.3rem 0.8rem',
                borderRadius: '20px',
                fontSize: '0.85rem',
                fontWeight: 'bold'
              }}>
                {unreadCount} nouveau{unreadCount > 1 ? 'x' : ''}
              </span>
            )}
          </h2>
          <button
            onClick={() => {
              setShowNewMessage(true);
              setSelectedMessage(null);
              setNewMessage({
                subject: '',
                message: '',
                receiver_id: null,
                receiver_type: user.role === 'driver' ? 'admin' : 'driver'
              });
            }}
            style={{
              padding: '0.7rem 1.5rem',
              background: '#B8472E',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            ✉️ Nouveau message
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '2rem' }}>
          {/* Liste des messages */}
          <div style={{ borderRight: '1px solid #e0e0e0', paddingRight: '1rem' }}>
            <h3 style={{ fontSize: '1.1rem', marginBottom: '1rem', color: '#555' }}>Conversations</h3>
            {messages.length === 0 ? (
              <p style={{ color: '#999', textAlign: 'center', padding: '2rem' }}>Aucun message</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                {messages.map(msg => (
                  <div
                    key={msg.id}
                    onClick={() => handleSelectMessage(msg)}
                    style={{
                      padding: '1rem',
                      background: selectedMessage?.id === msg.id ? '#e3f2fd' : (msg.is_read ? '#fff' : '#f0f8ff'),
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      position: 'relative'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)'}
                    onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
                  >
                    {!msg.is_read && (
                      <div style={{
                        position: 'absolute',
                        top: '0.5rem',
                        right: '0.5rem',
                        width: '10px',
                        height: '10px',
                        background: '#e74c3c',
                        borderRadius: '50%'
                      }} />
                    )}
                    <div style={{ fontWeight: msg.is_read ? 'normal' : 'bold', fontSize: '0.95rem', marginBottom: '0.3rem' }}>
                      {msg.sender_name}
                    </div>
                    <div style={{ fontSize: '0.9rem', color: '#333', marginBottom: '0.3rem' }}>
                      {msg.subject}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#999' }}>
                      {format(new Date(msg.created_at), 'dd/MM/yyyy HH:mm')}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Zone de lecture/écriture */}
          <div>
            {showNewMessage ? (
              <div>
                <h3 style={{ fontSize: '1.3rem', marginBottom: '1.5rem' }}>✉️ Nouveau message</h3>
                <form onSubmit={handleSendMessage}>
                  {/* Destinataire (admin uniquement) */}
                  {(user.role === 'admin' || user.role === 'manager') && (
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                        Destinataire
                      </label>
                      <select
                        value={newMessage.receiver_id || ''}
                        onChange={(e) => setNewMessage({ ...newMessage, receiver_id: parseInt(e.target.value) })}
                        required
                        style={{
                          width: '100%',
                          padding: '0.7rem',
                          borderRadius: '6px',
                          border: '1px solid #ddd'
                        }}
                      >
                        <option value="">Sélectionner un chauffeur</option>
                        {drivers.map(driver => (
                          <option key={driver.user_id} value={driver.user_id}>
                            {driver.firstname} {driver.lastname} ({driver.vehicle_plate || 'N/A'})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                      Sujet
                    </label>
                    <input
                      type="text"
                      value={newMessage.subject}
                      onChange={(e) => setNewMessage({ ...newMessage, subject: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '0.7rem',
                        borderRadius: '6px',
                        border: '1px solid #ddd'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                      Message
                    </label>
                    <textarea
                      value={newMessage.message}
                      onChange={(e) => setNewMessage({ ...newMessage, message: e.target.value })}
                      required
                      rows={8}
                      style={{
                        width: '100%',
                        padding: '0.7rem',
                        borderRadius: '6px',
                        border: '1px solid #ddd',
                        fontFamily: 'inherit',
                        resize: 'vertical'
                      }}
                    />
                  </div>

                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <button
                      type="submit"
                      style={{
                        padding: '0.7rem 1.5rem',
                        background: '#27ae60',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                      }}
                    >
                      📤 Envoyer
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowNewMessage(false)}
                      style={{
                        padding: '0.7rem 1.5rem',
                        background: '#95a5a6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer'
                      }}
                    >
                      Annuler
                    </button>
                  </div>
                </form>
              </div>
            ) : selectedMessage ? (
              <div>
                <div style={{ borderBottom: '2px solid #e0e0e0', paddingBottom: '1rem', marginBottom: '1.5rem' }}>
                  <h3 style={{ fontSize: '1.3rem', marginBottom: '0.5rem' }}>{selectedMessage.subject}</h3>
                  <div style={{ fontSize: '0.9rem', color: '#666' }}>
                    De: {selectedMessage.sender_name} • {format(new Date(selectedMessage.created_at), 'dd/MM/yyyy à HH:mm')}
                  </div>
                </div>

                {/* Conversation */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                  {conversation.map(msg => (
                    <div
                      key={msg.id}
                      style={{
                        padding: '1rem',
                        background: msg.sender_id === user.id ? '#e3f2fd' : '#f8f9fa',
                        borderRadius: '8px',
                        borderLeft: `4px solid ${msg.sender_id === user.id ? '#B8472E' : '#95a5a6'}`
                      }}
                    >
                      <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.5rem' }}>
                        <strong>{msg.sender_email}</strong> • {format(new Date(msg.created_at), 'dd/MM/yyyy HH:mm')}
                      </div>
                      <div style={{ whiteSpace: 'pre-wrap' }}>{msg.message}</div>
                    </div>
                  ))}
                </div>

                <div style={{ marginTop: '2rem' }}>
                  <button
                    onClick={handleReply}
                    style={{
                      padding: '0.7rem 1.5rem',
                      background: '#B8472E',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    ↩️ Répondre
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '4rem', color: '#999' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>💬</div>
                <p>Sélectionnez un message pour le lire</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Messages;
